import React, { useState, useRef, useEffect } from "react";
import GTM from "../GTM";
import PropTypes from "prop-types";
import Tooltip from "./ui/Tooltip";

const MultiSelect = (props) => {
    const {
        selectedOptions,
        setSelectedOptions,
        options,
        headerLabel,
        setEnableReset,
        setEnableApplyButton,
        handleGtmTags,
        gtmTagLabel,
        enableSearch,
        showCheckboxes = true,
        attribute2,
        activeAbsenceMessage,
    } = props;

    const activeAbsenceLabel = [""];

    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const gtm = new GTM(null);

    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        if (!isOpen) setFocusedIndex(0); // Reset focus to the first option (Select All)
    };

    const filteredOptions = options.filter((option) => {
        if (!searchQuery) return true;

        const isNumericSearch = /^\d+$/.test(searchQuery);
        const search = searchQuery.toLowerCase();

        if (isNumericSearch) {
            return (
                option?.value?.toString()?.startsWith(searchQuery) ||
                option?.name?.toLowerCase()?.startsWith(search)
            );
        } else {
            return option?.divisionName
                ?.toString()
                ?.toLowerCase()
                ?.startsWith(search);
        }
    });

    const isAllSelected =
        showCheckboxes &&
        filteredOptions.length > 0 &&
        filteredOptions.every((option) =>
            selectedOptions.some((selected) => selected.value === option.value)
        );

    const trackGTMEvent = ({ action, attribute4 }) => {
        gtm.Event(
            "custom_event",
            "filter",
            action,
            headerLabel,
            "report detail",
            attribute2,
            "filter_item_click",
            attribute4
        );
    };

    const handleOptionToggle = (index) => {
        const option = filteredOptions[index];
        if (showCheckboxes) {
            if (index === 0) {
                isAllSelected ? handleUnselectAll() : handleSelectAll();
            } else {
                const option = filteredOptions[index - 1];
                setSelectedOptions((prev) => {
                    const isSelected = prev.some(
                        (selected) => selected.value === option.value
                    );
                    trackGTMEvent({
                        action: isSelected ? "uncheck" : "check",
                        attribute4: filteredOptions[index - 1]?.value,
                    });
                    return isSelected
                        ? prev.filter(
                              (selected) => selected.value !== option.value
                          )
                        : [...prev, option];
                });
                setEnableReset(true);
                setEnableApplyButton(true);
            }
        } else {
            setSelectedOptions([option]);
            trackGTMEvent({
                action: option ? "check" : "uncheck",
                attribute4: option?.name,
            });
            setIsOpen(false);
            setEnableApplyButton(true);
            setEnableReset(true);
        }
    };

    const handleSelectAll = () => {
        setSelectedOptions(filteredOptions);
        setEnableReset(true);
        setEnableApplyButton(true);
        if (gtmTagLabel) handleGtmTags(gtmTagLabel);
    };

    const handleUnselectAll = () => {
        setSelectedOptions([]);
        setEnableReset(true);
        setEnableApplyButton(true);
    };
    useEffect(() => {
        const focusedElement = document.getElementById(
            `option-${focusedIndex}`
        );
        if (focusedElement) focusedElement.focus();
    }, [isOpen, focusedIndex]);

    const handleKeyDown = (e) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();

        if (isOpen) {
            switch (e.key) {
                case "ArrowDown":
                    setFocusedIndex((prev) =>
                        Math.min(filteredOptions.length, prev + 1)
                    );
                    break;
                case "ArrowUp":
                    setFocusedIndex((prev) => Math.max(0, prev - 1));
                    break;
                case "Enter":
                case " ":
                    handleOptionToggle(focusedIndex);
                    break;
                case "Escape":
                    setIsOpen(false);
                    break;
                default:
                    break;
            }
        } else if (
            e.key === "ArrowDown" ||
            e.key === "Enter" ||
            e.key === " "
        ) {
            setIsOpen(true);
            setFocusedIndex(0);
        }
    };
    useEffect(() => {
        if (isOpen) {
            const focusableElements = dropdownRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            const handleFocusTrap = (e) => {
                if (e.key === "Tab") {
                    if (e.shiftKey) {
                        // Shift + Tab
                        if (document.activeElement === firstElement) {
                            e.preventDefault();
                            lastElement.focus(); // Move focus to the last element
                        }
                    } else {
                        // Tab
                        if (document.activeElement === lastElement) {
                            e.preventDefault();
                            firstElement.focus(); // Move focus to the first element
                        }
                    }
                } else if (e.key === "Escape") {
                    setIsOpen(false); // Close on Escape
                }
            };

            document.addEventListener("keydown", handleFocusTrap);

            return () => {
                document.removeEventListener("keydown", handleFocusTrap);
            };
        }
    }, [isOpen]);

    const placeholderText = showCheckboxes
        ? isAllSelected && selectedOptions.length === options.length
            ? "All items are selected."
            : selectedOptions.length > 0
              ? selectedOptions.map((item) => item.name).join(", ")
              : "Add filters +"
        : selectedOptions?.length > 0
          ? selectedOptions.map((item) => item.name)
          : "Select";

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !toggleRef.current.contains(event.target)
            ) {
                setIsOpen(false);
                setSearchQuery("");
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const dropdownStyle =
        placeholderText === "Add filters +"
            ? { color: "#aaa", marginTop: "-6px" }
            : { color: "#fff", marginTop: "-6px" };

    const handleOptionFocus = (optionLabel, isSelected) => {
        const status = isSelected ? "checked" : "unchecked";
        const announcement = `${optionLabel} ${status}`;
        const liveRegion = document.getElementById("aria-live-region");
        if (liveRegion) {
            liveRegion.textContent = announcement;
            setTimeout(() => {
                liveRegion.textContent = "";
            }, 1000);
        }
    };

    return (
        <div
            className="col-lg-3 col-md-6 col-sm-4 col-6 col-print"
            style={{ position: "relative" }}
        >
            <label className="header pb-0" htmlFor={`${headerLabel}`}>
                {headerLabel}
            </label>
            <div
                ref={toggleRef}
                className="d-flex justify-content-between cursor-pointer customFilterSection"
                onClick={toggleDropdown}
                onKeyDown={handleKeyDown}
                role="combobox"
                aria-expanded={isOpen}
                aria-controls="multi-select-dropdown"
                aria-label={headerLabel}
                tabIndex={0}
                aria-activedescendant={`option-${focusedIndex}`}
                id={`${headerLabel}`}
            >
                <span id={headerLabel} className="sr-only">
                    Use the arrow keys to navigate options, Enter to select, and
                    Escape to close.
                </span>
                <div className="customFilterDropDown" style={dropdownStyle}>
                    {placeholderText}
                </div>
                <div
                    className="d-flex"
                    style={{
                        fontSize: 17,
                        paddingLeft: 8,
                        paddingRight: 8,
                        gap: 8,
                    }}
                >
                    {showCheckboxes && selectedOptions.length > 0 && (
                        <button
                            className="fa-solid fa-times border-0 bg-transparent d-flex text-white"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleUnselectAll();
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleUnselectAll();
                                }
                            }}
                            tabIndex={0}
                            aria-label="Clear selection"
                        ></button>
                    )}
                    <i
                        className={`fa-solid fa-angle-${isOpen ? "up" : "down"}`}
                    ></i>
                </div>
            </div>
            {isOpen && (
                <div className="custom-dropdown-container mt-5p">
                    <div
                        ref={dropdownRef}
                        id="multi-select-dropdown"
                        className="section-card custom-section-dropdown"
                        role="listbox"
                        aria-labelledby="multi-select-label"
                        onKeyDown={handleKeyDown}
                        aria-live="assertive"
                        style={{ paddingTop: 0 }}
                    >
                        {enableSearch && (
                            <div className="search-box-container">
                                <input
                                    type="text"
                                    className="search-box"
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                />
                            </div>
                        )}
                        <div
                            className="custom-scrollbar"
                            style={{
                                maxHeight: 250,
                                borderTop: enableSearch
                                    ? "1px solid #cdcdcd"
                                    : 0,
                            }}
                        >
                            {showCheckboxes && filteredOptions.length > 0 && (
                                <div
                                    className={`multi-select-option ${focusedIndex === 0 ? "focused" : ""}`}
                                    onClick={(e) => {
                                        handleOptionToggle(0);
                                        trackGTMEvent({
                                            headerLabel,
                                            action: e.target.checked
                                                ? "check"
                                                : "uncheck",
                                            attribute4: e.target.checked
                                                ? "select all"
                                                : "deselect all",
                                        });
                                    }}
                                    role="option"
                                    aria-checked={isAllSelected}
                                    id="option-0"
                                    onFocus={() =>
                                        handleOptionFocus(
                                            "Select All",
                                            isAllSelected
                                        )
                                    }
                                    tabIndex={-1}
                                >
                                    <input
                                        type="checkbox"
                                        className="custom-checkbox"
                                        checked={isAllSelected}
                                        onChange={() => handleOptionToggle(0)}
                                        aria-hidden="true"
                                        tabIndex="-1"
                                    />
                                    <label
                                        style={{
                                            marginLeft: 8,
                                            fontSize: "14px",
                                            fontWeight: 400,
                                        }}
                                    >
                                        Select All
                                    </label>
                                </div>
                            )}

                            {filteredOptions.length === 0 ? (
                                <div className="d-flex justify-content-center my-8p text-center">
                                    No options
                                </div>
                            ) : (
                                filteredOptions.map((option, index) => (
                                    <div
                                        key={option.value}
                                        className={`multi-select-option ${(focusedIndex === index + showCheckboxes ? 1 : 0) ? "focused" : ""}`}
                                        onClick={() =>
                                            handleOptionToggle(
                                                showCheckboxes
                                                    ? index + 1
                                                    : index
                                            )
                                        }
                                        role="option"
                                        aria-checked={
                                            showCheckboxes &&
                                            selectedOptions.some(
                                                (selected) =>
                                                    selected.value ===
                                                    option.value
                                            )
                                        }
                                        id={`option-${index + 1}`}
                                        tabIndex={-1}
                                        style={{
                                            backgroundColor:
                                                !showCheckboxes &&
                                                selectedOptions.some(
                                                    (selected) =>
                                                        selected.name ===
                                                        option.name
                                                )
                                                    ? "#037cb7"
                                                    : "transparent",
                                            color:
                                                !showCheckboxes &&
                                                selectedOptions.some(
                                                    (selected) =>
                                                        selected.name ===
                                                        option.name
                                                )
                                                    ? "white"
                                                    : "#525252",
                                        }}
                                    >
                                        {showCheckboxes && (
                                            <input
                                                type="checkbox"
                                                className="custom-checkbox"
                                                checked={selectedOptions.some(
                                                    (selected) =>
                                                        selected.value ===
                                                        option.value
                                                )}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                onKeyDown={(e) => {
                                                    if (
                                                        e.key === "Enter" ||
                                                        e.key === " "
                                                    ) {
                                                        e.preventDefault();
                                                        handleOptionToggle(
                                                            showCheckboxes
                                                                ? index + 1
                                                                : index
                                                        );
                                                    }
                                                }}
                                                onChange={() =>
                                                    handleOptionToggle(
                                                        showCheckboxes
                                                            ? index + 1
                                                            : index
                                                    )
                                                }
                                                aria-checked={
                                                    selectedOptions.some(
                                                        (selected) =>
                                                            selected.value ===
                                                            option.value
                                                    )
                                                        ? "true"
                                                        : "false"
                                                }
                                                aria-hidden="true"
                                                tabIndex="-1"
                                                role="option"
                                            />
                                        )}
                                        <div>
                                            <label
                                                className="cursor-pointer"
                                                style={{
                                                    marginLeft: 8,
                                                    fontSize: "14px",
                                                    fontWeight: 400,
                                                }}
                                            >
                                                {option.name}
                                            </label>
                                            {option?.name
                                                ?.toLowerCase()
                                                .includes("period") && (
                                                <Tooltip
                                                    content={
                                                        activeAbsenceMessage
                                                    }
                                                    tooltipId="reporting-period-tooltip"
                                                    tooltipClassName="reporting-dropdown-tooltip"
                                                    arrowClassName="reportingTooltipArrow"
                                                    icon
                                                    portal
                                                    iconAriaLabel="More information about reporting period"
                                                    iconColor={
                                                        selectedOptions?.some(
                                                            (selected) =>
                                                                selected.value ===
                                                                option.value
                                                        )
                                                            ? "white"
                                                            : "#015294"
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div
                id="aria-live-region"
                style={{ position: "absolute", left: "-9999px" }}
                aria-live="polite"
            ></div>
        </div>
    );
};

MultiSelect.propTypes = {
    headerLabel: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ).isRequired,
    selectedOptions: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ).isRequired,
    gtmTagLabel: PropTypes.string,
    enableSearch: PropTypes.bool,
    attribute2: PropTypes.string,
    showCheckboxes: PropTypes.bool,
    setSelectedOptions: PropTypes.func,
    setEnableReset: PropTypes.func,
    setEnableApplyButton: PropTypes.func,
    handleGtmTags: PropTypes.func,
    activeAbsenceMessage: PropTypes.string,
};

export default MultiSelect;
